import React, { useState } from 'react'
import styled from 'styled-components'
import Responsive from '../../assets/config/Responsive'
import CloseIcon from '../../assets/icons/JSX/CloseIcon'
import Colors from '../../assets/styles/Colors'
import { Scrollbar } from '../../assets/styles/GlobalStyle'
import Button from '../Button'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import { AppState } from '../../store/appSlice'
import { useAppSelector } from '../../store'
import en from 'date-fns/locale/en-US'
import ka from 'date-fns/locale/ka'
import { FaTimes } from 'react-icons/fa'

const ExportPoliciesModal: React.FC<{
  isOpen: boolean
  onClose: Function
  children: React.ReactNode
  onClick?: (dates: {
    registerDateFrom: string | undefined
    registerDateTo: string | undefined
    policyDateFrom: string | undefined
    policyDateTo: string | undefined
  }) => void
}> = ({ isOpen = false, onClose, children, onClick }) => {
  const { t } = useTranslation()
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)

  const [registerDateFrom, setRegisterDateFrom] = useState<Date | null>(new Date())
  const [registerDateTo, setRegisterDateTo] = useState<Date | null>(new Date())
  const [policyDateFrom, setPolicyDateFrom] = useState<Date | null>(null)
  const [policyDateTo, setPolicyDateTo] = useState<Date | null>(null)

  const handleExport = () => {
    const formatDate = (date: Date | null) => {
      if (!date) return undefined
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    if (onClick) {
      onClick({
        registerDateFrom: formatDate(registerDateFrom),
        registerDateTo: formatDate(registerDateTo),
        policyDateFrom: formatDate(policyDateFrom),
        policyDateTo: formatDate(policyDateTo),
      })
    }
  }

  return isOpen === false ? null : (
    <>
      <Wrapper>
        <MobileHeader>
          <HeaderText>
            <div>
              <span>{t('exportModal.title')}</span>
            </div>
          </HeaderText>
          <HeaderText>
            <div>
              <CloseModalBtnMobile onClick={() => onClose()}>
                <CloseIcon />
              </CloseModalBtnMobile>
            </div>
          </HeaderText>
        </MobileHeader>
        <Scrollbar hideScroll={isOpen} />
        <ShadowBackground onClick={() => onClose()} />
        <Content>
          <CloseModalBtnStyle onClick={() => onClose()}>
            <CloseIcon />
          </CloseModalBtnStyle>
          <PaymentInfo>
            <Title>{t('exportModal.title')}</Title>
            <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
              <DatePickerWrapper>
                <DatePickerLabel>{t('exportModal.issueDate')}</DatePickerLabel>
                <DatePicker
                  maxDate={new Date()}
                  minDate={new Date(new Date().setMonth(new Date().getMonth() - 3))}
                  selected={registerDateFrom}
                  onChange={setRegisterDateFrom}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText=""
                  locale={lang === 'ge' ? ka : en}
                  onFocus={(e) => e.target.blur()}
                  dateFormat="yyyy-MM-dd"
                />
              </DatePickerWrapper>
              <DatePickerWrapper style={{ marginTop: '28px' }}>
                <DatePicker
                  maxDate={new Date()}
                  minDate={new Date(new Date().setMonth(new Date().getMonth() - 3))}
                  selected={registerDateTo}
                  onChange={setRegisterDateTo}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText=""
                  locale={lang === 'ge' ? ka : en}
                  onFocus={(e) => e.target.blur()}
                  dateFormat="yyyy-MM-dd"
                />
              </DatePickerWrapper>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
              <DatePickerWrapper>
                <DatePickerLabel>{t('exportModal.insurancePeriod')}</DatePickerLabel>
                <div style={{ position: 'relative', width: '100%' }}>
                  <DatePicker
                    minDate={new Date(new Date().setMonth(new Date().getMonth() - 3))}
                    selected={policyDateFrom}
                    onChange={setPolicyDateFrom}
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    placeholderText=""
                    locale={lang === 'ge' ? ka : en}
                    onFocus={(e) => e.target.blur()}
                    dateFormat="yyyy-MM-dd"
                  />
                  {policyDateFrom && (
                    <ClearBtn
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPolicyDateFrom(null)
                      }}
                      aria-label="Clear"
                    >
                      <FaTimes />
                    </ClearBtn>
                  )}
                </div>
              </DatePickerWrapper>
              <DatePickerWrapper style={{ marginTop: '28px' }}>
                <div style={{ position: 'relative', width: '100%' }}>
                  <DatePicker
                    minDate={new Date(new Date().setMonth(new Date().getMonth() - 3))}
                    selected={policyDateTo}
                    onChange={setPolicyDateTo}
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    placeholderText=""
                    locale={lang === 'ge' ? ka : en}
                    onFocus={(e) => e.target.blur()}
                    dateFormat="yyyy-MM-dd"
                  />
                  {policyDateTo && (
                    <ClearBtn
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPolicyDateTo(null)
                      }}
                      aria-label="Clear"
                    >
                      <FaTimes />
                    </ClearBtn>
                  )}
                </div>
              </DatePickerWrapper>
            </div>
            <ContentStyles>
              <Button onClick={handleExport} text={t('exportModal.downloadButton')} fullWidth />
              <Button
                onClick={() => {
                  setRegisterDateFrom(new Date())
                  setRegisterDateTo(new Date())
                  setPolicyDateFrom(null)
                  setPolicyDateTo(null)
                }}
                text={t('exportModal.resetButton')}
                fullWidth
                style={{ marginTop: 12, background: Colors.secondaryGrey, color: Colors.primaryBlack }}
              />
            </ContentStyles>
          </PaymentInfo>
        </Content>
      </Wrapper>
    </>
  )
}

const ClearBtn = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 10;
  color: ${Colors.primaryGrey};
  &:hover {
    color: ${Colors.primaryRed};
  }
`

//
const MobileHeader = styled.div`
  display: none;
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: 64px;
  background-color: ${Colors.white};
  align-items: center;
  user-select: none;
  z-index: 2;
  -webkit-box-shadow: 0px 0px 15px 3px #88888811;
  box-shadow: 0px 0px 15px 3px #88888811;
  padding-left: 100px;
  padding-right: 100px;
  ${Responsive.mobile} {
    display: flex;
    justify-content: space-between;
    padding-left: 25px;
    padding-right: 25px;
    z-index: 101;
    /* height: 64px; */
  }
`
const ContentStyles = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px;
  padding-bottom: 50px;
  margin-bottom: 20px;
  /* border-bottom: 1px solid ${Colors.secondaryGrey}; */
  div {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  span:not(:last-of-type) {
    border-bottom: 2px solid ${Colors.secondaryGrey};
  }
  /*  */
  &:last-child {
    border: 0px;
    margin-bottom: 0px;
    padding-bottom: 10px;
  }
  ${Responsive.mobile} {
    display: flex;
    flex-direction: column;
    padding: 10px;
    padding-bottom: 30px;
    /* margin-bottom: 20px; */
    border-bottom: 1px solid ${Colors.secondaryGrey};
    div {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    /*  */
    &:last-child {
      border: 0px;
      margin-bottom: 0px;
      padding-bottom: 10px;
    }
  }
`

const DatePickerLabel = styled.div`
  font-size: 14px;
  line-height: 16px;
  margin-bottom: 12px;
  text-align: left;
  padding-right: 24px;
  color: ${Colors.primaryGrey};
  margin-right: auto;
`

const DatePickerWrapper = styled.div`
  //react-datepicker
  display: flex;
  flex-direction: column;
  align-items: center;
                  popperPlacement="auto"
                  />
  margin: 0 auto;
  margin-bottom: 24px;
  /*  */
  .react-datepicker {
    font-family: inherit;
  }
  /*  */
  .react-datepicker__header__dropdown.react-datepicker__header__dropdown--select {
    display: flex;
    justify-content: space-between;
    align-items: center;

    div {
      width: 45%;
      margin-block: 10px;
      select {
        width: 100%;
        height: 30px;
        border-radius: 4px;
        border: unset;
        outline: 1px solid ${Colors.primaryBlack};
        background-color: ${Colors.white};
        color: ${Colors.primaryBlack};
        font-weight: 600;
      }
    }
  }
  /* styles with className */
  .react-datepicker-wrapper {
    width: 100%;
    /* visibility: hidden; */
  }
  .react-datepicker__input-container {
    input {
      width: 100%;
      outline: none;
      border: 1px solid ${Colors.secondaryGrey};
      border-radius: 4px;
      height: 57px;
      padding: 18px 22px;
      color: ${Colors.primaryBlack};
    }
  }
  .react-datepicker-ignore-onclickoutside {
    width: 100%;
  }
  .react-datepicker__triangle {
    display: none;
  }
  .react-datepicker__navigation--previous {
    left: -12px;
  }
  .react-datepicker__navigation--next {
    right: -12px;
  }
  .react-datepicker-popper {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 9999 !important;
    padding: 30px 35px;
    background-color: ${Colors.white};
    -webkit-box-shadow: 0px 0px 15px 3px #88888811;
    box-shadow: 0px 0px 15px 3px #88888811;
    border-radius: 8px;
    z-index: 2147483647 !important; /* Maximum z-index, always on top */
    /* Remove forced center positioning so popper appears near input */
    ${Responsive.tablet} {
      padding: 30px 35px;
    }
    ${Responsive.mobile} {
      padding: 20px 12.5px 25px 12.5px;
      width: 95vw;
      min-width: unset;
      max-width: unset;
    }
  }
  .react-datepicker__current-month {
    font-size: 18px;
    color: ${Colors.primaryBlack};
    font-weight: 500;
    line-height: 9px;
    margin-bottom: 20px;
  }
  .react-datepicker {
    border: 0px;
  }
  .react-datepicker__header:not(.react-datepicker__header--has-time-select) {
    border-top-left-radius: 8px;
  }
  .react-datepicker__header:not(.react-datepicker__header--has-time-select) {
    border-top-right-radius: 8px;
  }
  .react-datepicker__header {
    background-color: ${Colors.white};
    border-bottom: 0px;
  }
  .react-datepicker__day--selected {
    background-color: ${Colors.primaryRed} !important;
    color: ${Colors.white} !important;
    outline: none;
  }
  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: 40px;
    /* height: 46px; */
    border-radius: 50% !important;
    text-align: center;
    line-height: 40px;
    font-size: 14px;
    font-weight: normal;
    color: ${Colors.primaryGrey};

    ${Responsive.mobile} {
      width: 35px;
      line-height: 35px;
      font-size: 13px;
    }
  }
  .react-datepicker__day--selected:hover {
    background-color: ${Colors.primaryRed} !important;
    border-radius: 50% !important;
    font-size: 14px;
  }
  .react-datepicker__day:hover {
    background-color: ${Colors.primaryRed} !important;
    border-radius: 50% !important;
    color: ${Colors.white};
    font-size: 14px;
  }
  .react-datepicker__day--in-range {
    background-color: ${Colors.secondaryGrey};
    color: ${Colors.primaryBlack};
    border-radius: 0px;
  }
  .react-datepicker__day--in-selecting-range:not(
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--in-range
    ) {
    background-color: ${Colors.secondaryGrey};
  }
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__year-text--keyboard-selected {
    background-color: transparent;
  }
  /* disable days */
  .react-datepicker__day--disabled,
  .react-datepicker__month-text--disabled,
  .react-datepicker__quarter-text--disabled,
  .react-datepicker__year-text--disabled {
    user-select: none;
    pointer-events: none;
    opacity: 0.3;
  }
  .react-datepicker__portal {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 2000 !important;
  }
  .custom-clear-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    z-index: 10;
    color: ${Colors.primaryGrey};
    &:hover {
      color: ${Colors.primaryRed};
    }
    svg {
      width: 16px;
      height: 16px;
      pointer-events: none;
    }
  }
`

const Text = styled.div`
  font-size: 15px;
  color: ${Colors.primaryGrey};
  user-select: none;
  margin-bottom: 5px;
  /* responsive */
  ${Responsive.mobile} {
    font-size: 16px;
    margin-left: 0;
    padding-left: 0;
  }
`
const HeaderText = styled.div`
  ${Responsive.mobile} {
    color: ${Colors.primaryBlack};
    user-select: none;
    margin-bottom: 5px;
    font-size: 16px;
    margin-left: 0;
    padding-left: 0;
  }
`
const CloseModalBtn = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: auto;
  border: 0px;
  background-color: transparent;
  outline: none;
  padding: 10px;
  margin-right: -10px;
  margin-top: -10px;
  svg {
    width: 14px;
    height: 14px;
    color: ${Colors.rollingStone};
  }
  /*  */
  &:hover {
    svg {
      color: ${Colors.primaryBlack};
    }
  }
`
const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  z-index: 101;
  ${Responsive.mobile} {
    background-color: ${Colors.grey};
    display: block;
    overflow-y: scroll;
  }
`
const ShadowBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5;
  background-color: black;
  opacity: 0.3;
  width: 100%;
  height: 100vh;
  ${Responsive.mobile} {
    display: none;
  }
`
const Content = styled.div`
  padding: 24px;
  background-color: white;
  z-index: 6;
  border-radius: 8px;
  position: relative;
  min-width: 550px;
  max-width: 550px;
  min-height: 20vh;
  max-height: 80vh;
  overflow-y: scroll;
  padding-bottom: 50px;
  /*  */
  ${Responsive.mobile} {
    padding: 25px 10px 50px;
    min-width: unset;
    margin: 40px 25px;
    border-radius: 8px;
    padding-bottom: 40px;
    max-width: unset;
    max-height: 80vh;
    overflow-y: scroll;
  }
`
const Amount = styled.div`
  background-color: ${Colors.lightGrey};
  border-radius: 5px;
  padding: 18px 20px;
  margin-top: 30px;
  ${Responsive.mobile} {
    margin-top: 20px;
    padding: 5px 25px 15px;
  }
`
const AmountText = styled.div`
  font-size: 15px;
  color: ${Colors.primaryBlack};
  user-select: none;
  /* responsive */
  ${Responsive.mobile} {
    margin-left: 0;
    padding-left: 0;
    margin-top: 10px;
  }
`
const Title = styled.div`
  display: flex;
  text-align: center;
  justify-content: center;
  margin-bottom: 20px;
  color: ${Colors.primaryBlack};
  font-size: 26px;
  ${Responsive.mobile} {
    font-size: 16px;
    text-align: left;
    justify-content: flex-start;
    padding-left: 10px;
  }
`
const CloseModalBtnStyle = styled(CloseModalBtn)`
  ${Responsive.mobile} {
    display: none;
  }
`
const CloseModalBtnMobile = styled(CloseModalBtn)`
  margin-top: 1px;
`
const PaymentInfo = styled.div``
const InsuranceText = styled.div`
  text-align: right;
`
const Info = styled.span`
  padding-top: 10px;
  padding-bottom: 10px;
`
const InfoBox = styled.div`
  display: flex;
  align-items: center;
  margin-block: 15px;
  max-width: 464px;
  margin-bottom: 0;
  margin-right: auto;

  svg {
    height: 24px;
    width: 24px;
    color: ${Colors.primaryRed};
  }
`
const InfoText = styled.p`
  margin-top: 5px;
  margin-bottom: 0px;
  margin-left: 10px;
  font-size: 12px;
  color: ${Colors.primaryGrey};
`

export default ExportPoliciesModal
